import { setSelectionRange } from '@testing-library/user-event/dist/utils';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../helpers/Loader';
import { properties } from '../../helpers/properties';
import ToolTip from '../../helpers/ToolTip';
import ErrorMsg from '../ErrorMsg/ErrorMsg';
//css
import './SearchPage.css';

export default function SearchPage({ authToken }) {
    const SEARCH_URL = process.env.REACT_APP_SEARCH_URL;
    const { ERROR_MESSAGE, TOOLTIP_CONTENT } = properties;
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState("1month");
    const [tokenNumber, setTokenNumber] = useState("");
    const [cardNumber, setCardNumber] = useState({ first6: "", last4: "" });
    const [transactionId, setTransactionId] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    useEffect(() => {
        errorMsg && window.scrollTo({ top: 0, left: 0, behavior: "smooth", })
    }, [errorMsg])

    const validateForm = () => {
        let formError = '';
        if (isTokenValid()) {
            if (!startDate && !endDate && !tokenNumber && !transactionId && !cardNumber.first6 && !cardNumber.last4) {
                formError = ERROR_MESSAGE.EMPTY_INPUT;
            }
            else if (startDate && endDate && !tokenNumber && !transactionId && !cardNumber.first6 && !cardNumber.last4) {
                formError = ERROR_MESSAGE.ONLY_DATE_RANGE;
            } else if ((startDate && !endDate) || (!startDate && endDate)) {
                formError = ERROR_MESSAGE.INVALID_DATE_RANGE;
            } else if (startDate && endDate && startDate > endDate) {
                formError = ERROR_MESSAGE.INVALID_DATE;
            } else if (cardNumber.last4 && (!/^\d{6}$/.test(cardNumber.first6) || !cardNumber.first6)) {
                formError = ERROR_MESSAGE.EMPTY_FIRST6;
            } else if (cardNumber.first6 && (!/^\d{4}$/.test(cardNumber.last4) || !cardNumber.last4)) {
                formError = ERROR_MESSAGE.EMPTY_LAST4;
            } else if (!tokenNumber && !transactionId && !(cardNumber.first6 && cardNumber.last4)) {
                formError = ERROR_MESSAGE.EMPTY_INPUT
            }
            else {
                setErrorMsg("");
            }
        }
        else {
            formError = ERROR_MESSAGE.INVALID_TOKEN;
        }
        return formError;
    }

    const isTokenValid = () => {
        if(!tokenNumber || tokenNumber.trim() === "") {
            return true;
        }
        if(tokenNumber && tokenNumber[0] === "T"){
            return true;
        }
        if (tokenNumber && (tokenNumber[0] === "S" && tokenNumber.length === 17 && (/S\d{16}/.test(tokenNumber)))) {
            return true;
        }
        return false;
    }

    const handleDropdownChange = (e) => {
        const range = e.target.value;
        setDateRange(range);
        const today = new Date();
        const formattedTodaysDate = new Date().toISOString().split('T')[0];

        switch (range) {
            case '3months':
                const last3months = new Date(today.setMonth(today.getMonth() - 3));
                const formattedNext3Month = last3months.toISOString().split('T')[0];

                setStartDate(formattedNext3Month)
                setEndDate(formattedTodaysDate);
                break;
            case '1month':
                const last1month = new Date(today.setMonth(today.getMonth() - 1));
                const formattedNext1Month = last1month.toISOString().split('T')[0];

                setStartDate(formattedNext1Month)
                setEndDate(formattedTodaysDate)
                break;
            case '2weeks':
                const twoWeekAgo = new Date(today)
                twoWeekAgo.setDate(twoWeekAgo.getDate() - 14);
                const formattedNext2Week = twoWeekAgo.toISOString().split('T')[0];

                setStartDate(formattedNext2Week)
                setEndDate(formattedTodaysDate)
                break;
            default:
                setStartDate("")
                setEndDate("")
                break;
        }

    }

    const handleSearch = async (e) => {
        e.preventDefault();
        console.log("Search initiated");
        setErrorMsg("");
        setIsLoading(true);
        let stDate = "", lstDate = "";
        if (startDate && endDate) {
            stDate = startDate + "T00:00:00.000Z";
            lstDate = endDate + "T23:59:59.999Z";
        }
        const queryStringParameters = {
            token: tokenNumber,
            transId: transactionId,
            first6: cardNumber.first6,
            last4: cardNumber.last4,
            startDate: stDate,
            endDate: lstDate
        }
        const validationError = validateForm();
        setErrorMsg(validationError);
        if (!validationError) {
            try {
                let config = {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                    params: queryStringParameters,
                }
                await axios.get(SEARCH_URL, config).then(
                    (res) => {
                        if (res.data.length <= 0) {
                            setErrorMsg(ERROR_MESSAGE.GENERIC_ERROR);
                        }
                        else {
                            setErrorMsg("");
                            navigate("/result", { state: { transactions: res.data, authToken: authToken } })
                        }
                    })
            }
            catch (e) {
                setErrorMsg(ERROR_MESSAGE.GENERIC_ERROR);
            }
            finally {
                setIsLoading(false);
            }
        }
        else {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const default1month = { target: { value: "1month" } }
        handleDropdownChange(default1month);
    }, [])

    const formClassName = isLoading ? 'search-page-form loader-enabled' : 'search-page-form';

    return (
        <div className="search-page-container">
            <div id="search-error" className="error-container">{errorMsg && <ErrorMsg errorMsg={errorMsg} />}</div>

            <div className='search-form-wrapper'>
                {isLoading && <Loader />}

                <form className={formClassName} onSubmit={handleSearch}>
                    <div className="date-range-section form-group">
                        <label htmlFor="date-range-dropdown">Date Range:
                            <ToolTip content={TOOLTIP_CONTENT.DATE_RANGE_TOOLTIP} />
                        </label>
                        <div className="date-range-picker">
                            <select id="date-range-dropdown" value={dateRange} onChange={handleDropdownChange}>
                                <option value="custom">Custom</option>
                                <option value="3months">Last 3 Months</option>
                                <option value="1month">Last Month</option>
                                <option value="2weeks">Last 2 Weeks</option>
                            </select>
                            <input
                                className="search-page-date"
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value)
                                }}
                                max={new Date().toJSON().slice(0, 10)}
                                readOnly={dateRange !== "custom"}
                            />
                            <input
                                className="search-page-date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                max={new Date().toJSON().slice(0, 10)}
                                readOnly={dateRange !== "custom"}
                            />
                        </div>
                    </div>
                    <div className="token-section form-group">
                        <label htmlFor="token-number">Token Number:
                            <ToolTip content={TOOLTIP_CONTENT.TOKEN_NUMBER_TOOLTIP} /></label>
                        <input
                            id="token-number"
                            className="search-page-input"
                            type="text"
                            value={tokenNumber}
                            onChange={(e) => setTokenNumber(e.target.value)}
                            placeholder="Search by Token Number"
                        />
                    </div>
                    <div className="transaction-id form-group">
                        <label htmlFor="transaction-id">Application Transaction ID:</label>
                        <input
                            id="transaction-id"
                            className="search-page-input"
                            type="text"
                            value={transactionId}
                            onChange={(e) => setTransactionId(e.target.value)}
                            placeholder="Search by Transaction ID"
                        />
                    </div>
                    <div className="card-section form-group">
                        <label>Card Number:<ToolTip content={TOOLTIP_CONTENT.CARD_NUMBER_TOOLTIP} /></label>
                        <div id="card-digits" className="card-number">
                            <input
                                className="search-page-input card-first6"
                                type="text"
                                id="cardfirst6"
                                value={cardNumber.first6}
                                onChange={(e) =>
                                    setCardNumber({ ...cardNumber, first6: e.target.value })
                                }
                                placeholder="First 6 digits"
                                maxLength={6}
                            />
                            <input
                                className="search-page-input card-last4"
                                type="text"
                                id="cardlast4"
                                value={cardNumber.last4}
                                onChange={(e) =>
                                    setCardNumber({ ...cardNumber, last4: e.target.value })
                                }
                                placeholder="Last 4 digits"
                                maxLength={4}
                            />
                        </div>
                    </div>
                    <div className="search-button">
                        <button type="submit">Submit</button>
                    </div>
                </form>
            </div>

        </div>
    );
}
